import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useAuthStore } from '../store/authStore';
import { leaderboardApi } from '../services/api';

interface LeaderboardUser {
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    telegramId: string;
  };
  totalXP: number;
  rank: number;
}

const Leaderboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [currentUserRank, setCurrentUserRank] = useState<number | null>(null);
  const [currentUserXP, setCurrentUserXP] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await leaderboardApi.getWeeklyLeaderboard();
      setLeaderboard(response.leaderboard || []);
      setCurrentUserRank(response.currentUser?.rank || null);
      setCurrentUserXP(response.currentUser?.xp || 0);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPoints = (points: number) => {
    return points.toLocaleString('en-US');
  };

  const getUserDisplayName = (userData: LeaderboardUser['user']) => {
    if (userData.firstName || userData.lastName) {
      return `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
    }
    if (userData.username) {
      return `@${userData.username}`;
    }
    return `User ${userData.telegramId?.slice(-4) || 'Unknown'}`;
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const getPrizeAmount = (rank: number): number | null => {
    if (rank === 1) return 50;
    if (rank === 2) return 30;
    if (rank === 3) return 20;
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 pb-24">
        <div className="bg-tg-secondary rounded-xl p-6 border border-gray-800">
          <div className="animate-pulse space-y-3">
            <div className="h-6 bg-gray-700 rounded w-1/3"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-tg-hint hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('common.back')}</span>
        </button>
        <h1 className="text-2xl font-bold text-white mb-2">
          {t('checkIn.weeklyLeaderboard') || 'Weekly XP Leaderboard'}
        </h1>
        <p className="text-sm text-tg-hint">
          {t('checkIn.leaderboardSubtitle') || 'Top performers this week'}
        </p>
      </div>

      {/* Top 3 Prizes Box */}
      <div className="mb-6 bg-tg-secondary rounded-xl p-6 border border-gray-800">
        <h2 className="text-lg font-bold text-white mb-4 text-center">
          {t('checkIn.prize') || 'Prize'}
        </h2>
        <div className="flex items-end justify-center gap-4">
          {/* 2nd Place */}
          {leaderboard.length > 1 && leaderboard[1] && (
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className="text-4xl mb-2">🥈</div>
              <div className="text-sm font-semibold text-white mb-1 text-center w-full px-1 truncate">
                {getUserDisplayName(leaderboard[1].user)}
              </div>
              <div className="text-xs text-tg-hint mb-2 text-center">
                {formatPoints(leaderboard[1].totalXP)} XP
              </div>
              <div className="text-lg font-bold text-yellow-400 text-center">
                ${getPrizeAmount(2)}
              </div>
            </div>
          )}
          
          {/* 1st Place */}
          {leaderboard.length > 0 && leaderboard[0] && (
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className="text-5xl mb-2">🥇</div>
              <div className="text-sm font-semibold text-white mb-1 text-center w-full px-1 truncate">
                {getUserDisplayName(leaderboard[0].user)}
              </div>
              <div className="text-xs text-tg-hint mb-2 text-center">
                {formatPoints(leaderboard[0].totalXP)} XP
              </div>
              <div className="text-xl font-bold text-yellow-400 text-center">
                ${getPrizeAmount(1)}
              </div>
            </div>
          )}
          
          {/* 3rd Place */}
          {leaderboard.length > 2 && leaderboard[2] && (
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className="text-4xl mb-2">🥉</div>
              <div className="text-sm font-semibold text-white mb-1 text-center w-full px-1 truncate">
                {getUserDisplayName(leaderboard[2].user)}
              </div>
              <div className="text-xs text-tg-hint mb-2 text-center">
                {formatPoints(leaderboard[2].totalXP)} XP
              </div>
              <div className="text-lg font-bold text-yellow-400 text-center">
                ${getPrizeAmount(3)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Current User Rank Card */}
      {currentUserRank !== null && (
        <div className="mb-6 p-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-xl border-2 border-primary/50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-tg-hint mb-1">
                {t('checkIn.yourRank') || 'Your Rank'}
              </div>
              <div className="text-3xl font-black text-primary">
                #{currentUserRank}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-tg-hint mb-1">
                {t('checkIn.yourXP') || 'Your XP'}
              </div>
              <div className="text-2xl font-bold text-white">
                {formatPoints(currentUserXP)} XP
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      <div className="bg-tg-secondary rounded-xl p-4 border border-gray-800">
        <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
          {leaderboard.length === 0 ? (
            <div className="text-center py-12 text-tg-hint">
              {t('checkIn.noLeaderboardData') || 'No leaderboard data yet'}
            </div>
          ) : (
            leaderboard.map((entry, index) => {
              const isCurrentUser = user && entry.userId === user.id;
              const rankIcon = getRankIcon(entry.rank);
              
              return (
                <div
                  key={entry.userId}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                    isCurrentUser
                      ? 'bg-primary/20 border-2 border-primary/50'
                      : index < 3
                      ? 'bg-gray-800/50 border border-gray-700'
                      : 'bg-gray-800/30 border border-gray-800 hover:bg-gray-800/50'
                  }`}
                >
                  {/* Rank */}
                  <div className="flex-shrink-0 w-10 text-center">
                    {rankIcon ? (
                      <span className="text-2xl">{rankIcon}</span>
                    ) : (
                      <span className={`text-base font-bold ${
                        index < 3 ? 'text-primary' : 'text-tg-hint'
                      }`}>
                        #{entry.rank}
                      </span>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-primary">
                          {getUserDisplayName(entry.user).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-base font-semibold truncate ${
                          isCurrentUser ? 'text-primary' : 'text-white'
                        }`}>
                          {getUserDisplayName(entry.user)}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-primary/80">(You)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* XP */}
                  <div className="flex-shrink-0">
                    <div className="text-base font-bold text-primary">
                      {formatPoints(entry.totalXP)} XP
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
